import type { Direction } from './locale.utils';
import { rem } from './rem';
import { theme } from './theme';
import { BorderRadiusThemeProps, BorderThemeProps } from '../common/themes/theme';

export const sidebarWidth = 80;
export const configWidth = 316;
export const themeSettingsWidth = 760;
export const stageSpaceX = 54;

type Overrides = {
  borderColor?: string | null;
  borderWidth?: number;
  borderStyle?: string | null;
};

export const applyUnit = (value: string | number, unit = '1px') => {
  // Applies unit to either value or CSS variable
  return `calc(${unit === '1px' ? rem(value) : `${value} * ${unit}`})`;
};

export const getThemeBorderStyleAsOneString = (
  border: BorderThemeProps,
  globalBorderColor = theme.other?.['global']?.content?.colors?.decoration?.main,
  overrides: Overrides
) => {
  const borderStyle = getThemeBorderStyle(border, globalBorderColor, overrides);
  return `${rem(borderStyle.borderWidth)} ${borderStyle.borderStyle} ${borderStyle.borderColor}`;
};

//DONE as theme-border-style mixin
export const getThemeBorderStyle = (border: BorderThemeProps, globalBorderColor?: string, overrides?: Overrides) => {
  return getThemeBorderStyleTheme(border, globalBorderColor, overrides);
};

const getThemeBorderStyleTheme = (border: BorderThemeProps, globalBorderColor?: string, overrides?: Overrides) => {
  const { borderColor, borderWidth, borderStyle } = border;

  return {
    borderColor: overrides?.borderColor ?? borderColor ?? globalBorderColor,
    borderStyle: overrides?.borderStyle ?? borderStyle,
    borderWidth: overrides?.borderWidth ?? borderWidth
  };
};

const getThemeBorderRadiusStyleTheme = (
  borderRadius: BorderRadiusThemeProps,
  overrides?: Partial<BorderRadiusThemeProps>,
  direction: Direction = 'ltr'
) => {
  const { topLeft, topRight, bottomLeft, bottomRight, measureUnit } = borderRadius;

  const effectiveTopLeft = overrides?.topLeft ?? topLeft;
  const effectiveTopRight = overrides?.topRight ?? topRight;
  const effectiveBottomLeft = overrides?.bottomLeft ?? bottomLeft;
  const effectiveBottomRight = overrides?.bottomRight ?? bottomRight;
  const effectiveMeasureUnit = overrides?.measureUnit ?? measureUnit;

  const resultTopLeft = direction === 'rtl' ? effectiveTopRight : effectiveTopLeft;
  const resultTopRight = direction === 'rtl' ? effectiveTopLeft : effectiveTopRight;
  const resultBottomLeft = direction === 'rtl' ? effectiveBottomRight : effectiveBottomLeft;
  const resultBottomRight = direction === 'rtl' ? effectiveBottomLeft : effectiveBottomRight;

  return {
    borderRadius: `${applyUnit(resultTopLeft, effectiveMeasureUnit)} ${applyUnit(
      resultTopRight,
      effectiveMeasureUnit
    )} ${applyUnit(resultBottomRight, effectiveMeasureUnit)} ${applyUnit(resultBottomLeft, effectiveMeasureUnit)}`
  };
};

export const getThemeBorderRadiusStyle = (
  borderRadius: Partial<BorderRadiusThemeProps> | BorderRadiusThemeProps,
  overrides?: Partial<BorderRadiusThemeProps>,
  direction: Direction = 'ltr'
) => {
  return getThemeBorderRadiusStyleTheme(borderRadius as BorderRadiusThemeProps, overrides, direction);
};

export const PROSE_MIRROR_SELECTOR_CHILDREN = 'h1,h2,h3,h4,p,label,div,span';

/**
 * @description This selector is used to style the content of the ProseMirror editor
 */
export const PROSE_MIRROR_SELECTOR = PROSE_MIRROR_SELECTOR_CHILDREN.split(',')
  .map(child => `.ProseMirror ${child}`)
  .join(',');

/**
 * @description This selector is used to style the content of the ProseMirror preview
 */
export const PREVIEW_CONTENT_SELECTOR = PROSE_MIRROR_SELECTOR_CHILDREN.split(',')
  .map(child => `.previewContent ${child}`)
  .join(',');
